import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  messageFromShipmentFileError,
  useDeletePackagingShipment,
  useDownloadPackagingShipmentDeclarations,
  useExportPackagingShipmentEprSpreadsheet,
  useExportPackagingShipmentSpreadsheet,
  useGetPackagingShipment,
  useStartPackagingShipmentCheck,
} from '@/shared/hooks/api/packagingShipments'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { EditShipmentDialog } from './EditShipmentDialog'
import { ShipmentCheckResult } from './ShipmentCheckResult'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'

function formatUpdatedAt(value: string, locale: string): string {
  return new Date(value).toLocaleString(locale)
}

export function ShipmentDetailPage() {
  const { shipmentId = '' } = useParams<{ shipmentId: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('packagingSuppliers')
  const canWrite = useCanWriteOrgData()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [updateOpen, setUpdateOpen] = useState(false)
  const [editor, setEditor] = useState<'name' | 'note' | 'skus' | null>(null)
  const { data, isLoading, isError } = useGetPackagingShipment(shipmentId)
  const deleteShipment = useDeletePackagingShipment(shipmentId)
  const startCheck = useStartPackagingShipmentCheck(shipmentId)
  const downloadDeclarations = useDownloadPackagingShipmentDeclarations(shipmentId)
  const exportSpreadsheet = useExportPackagingShipmentSpreadsheet(shipmentId)
  const exportEprSpreadsheet =
    useExportPackagingShipmentEprSpreadsheet(shipmentId)
  const exportPending =
    exportSpreadsheet.isPending || exportEprSpreadsheet.isPending
  const wasRunning = useRef(false)

  useEffect(() => {
    if (data?.checkRunning) {
      wasRunning.current = true
      return
    }
    if (wasRunning.current && data?.failureMessage != null) {
      toast.error(t('shipmentsPage.detail.checkCouldNotBeCompleted'))
    }
    wasRunning.current = false
  }, [data?.checkRunning, data?.failureMessage, t])

  const shell = (content: ReactNode) => (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main className='flex flex-col gap-6 px-6'>
        {content}
      </Main>
    </>
  )

  if (isLoading) {
    return shell(
      <div className='py-12 text-center text-muted-foreground'>
        {t('shipmentsPage.loading')}
      </div>
    )
  }

  if (isError || data == null) {
    return shell(
      <div className='space-y-3'>
        <h2 className='text-2xl font-bold tracking-tight'>
          {t('shipmentsPage.detail.notFound')}
        </h2>
        <Link
          to='/packaging/shipments'
          className='text-sm text-primary underline'
        >
          {t('pages.shipments.title')}
        </Link>
      </div>
    )
  }

  let lastCheckedDisplay = t('shipmentsPage.detail.notCheckedYet')
  if (data.lastCheckedAt != null) {
    lastCheckedDisplay = formatUpdatedAt(data.lastCheckedAt, i18n.language)
  }

  let statusAlert: string | null = null
  if (data.checkRunning) {
    if (data.lastCheckedAt == null) {
      statusAlert = t('shipmentsPage.detail.updatingCreate')
    } else {
      statusAlert = t('shipmentsPage.detail.updatingReplace', {
        datetime: lastCheckedDisplay,
      })
    }
  } else if (data.failureMessage != null) {
    if (
      data.failureMessage ===
        'The update failed. The result below is from the last successful check.' &&
      data.lastCheckedAt != null
    ) {
      statusAlert = t('shipmentsPage.detail.failedWithResult', {
        datetime: lastCheckedDisplay,
      })
    } else {
      statusAlert = t('shipmentsPage.detail.failedFirst')
    }
  }

  let statusBadge: ReactNode
  if (data.checkRunning) {
    statusBadge = (
      <Badge variant='warning'>
        <Loader2 className='animate-spin' aria-hidden />
        {t('shipmentsPage.status.updating')}
      </Badge>
    )
  } else if (data.failureMessage != null) {
    statusBadge = (
      <Badge variant='destructive'>
        {t('shipmentsPage.detail.statusFailed')}
      </Badge>
    )
  } else {
    statusBadge = (
      <Badge variant='success'>{t('shipmentsPage.status.checked')}</Badge>
    )
  }

  const noteDisplay =
    data.note != null && data.note.length > 0 ? data.note : '—'
  const updateBusy = data.checkRunning || startCheck.isPending

  const requestUpdate = () => {
    if (data.displayedCheck != null) {
      setUpdateOpen(true)
      return
    }
    startCheck.mutate()
  }

  let downloadDisabledReason: string | null = null
  if (data.displayedCheck == null) {
    downloadDisabledReason = t('shipmentsPage.detail.availableAfterFirstCheck')
  } else if (data.displayedCheck.declarationsReadyCount === 0) {
    downloadDisabledReason = t('shipmentsPage.detail.noDeclarationsReady')
  }

  const downloadDeclarationsButton = (
    <Button
      type='button'
      size='sm'
      variant='outline'
      disabled={downloadDisabledReason != null || downloadDeclarations.isPending}
      onClick={() => {
        downloadDeclarations.mutate(undefined, {
          onError: (error) => {
            void messageFromShipmentFileError(
              error,
              t('shipmentsPage.detail.downloadFailed')
            ).then((message) => {
              toast.error(message)
            })
          },
        })
      }}
    >
      {downloadDeclarations.isPending ? (
        <Loader2 className='animate-spin' aria-hidden />
      ) : (
        <Download aria-hidden />
      )}
      {t('shipmentsPage.detail.downloadDeclarations')}
    </Button>
  )

  return shell(
    <div className='flex flex-col gap-6'>
      <div className='shrink-0 space-y-2'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='flex flex-wrap items-center gap-2'>
            <h2 className='text-2xl font-bold tracking-tight'>{data.name}</h2>
            {canWrite && (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground'
                disabled={data.checkRunning}
                aria-label={t('shipmentsPage.detail.editNameAria')}
                onClick={() => setEditor('name')}
              >
                <Pencil className='size-4' />
              </Button>
            )}
            {statusBadge}
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            {downloadDisabledReason == null ? (
              downloadDeclarationsButton
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex' tabIndex={0}>
                    {downloadDeclarationsButton}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{downloadDisabledReason}</TooltipContent>
              </Tooltip>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  disabled={exportPending}
                >
                  <FileSpreadsheet aria-hidden />
                  {t('shipmentsPage.detail.exportSpreadsheet')}
                  <ChevronDown aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  disabled={data.displayedCheck == null}
                  title={
                    data.displayedCheck == null
                      ? t('shipmentsPage.detail.availableAfterFirstCheck')
                      : undefined
                  }
                  onClick={() => {
                    exportSpreadsheet.mutate(undefined, {
                      onError: (error) => {
                        void messageFromShipmentFileError(
                          error,
                          t('export.failed')
                        ).then((message) => {
                          toast.error(message)
                        })
                      },
                    })
                  }}
                >
                  {t('shipmentsPage.result.epr.complianceTab')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    exportEprSpreadsheet.mutate(undefined, {
                      onError: (error) => {
                        void messageFromShipmentFileError(
                          error,
                          t('export.failed')
                        ).then((message) => {
                          toast.error(message)
                        })
                      },
                    })
                  }}
                >
                  {t('shipmentsPage.result.epr.tab')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {canWrite && (
              <Button
                type='button'
                size='sm'
                variant='destructive'
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 aria-hidden />
                {t('shipmentsPage.detail.deleteShipment')}
              </Button>
            )}
          </div>
        </div>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to='/packaging/shipments'>
                  {t('pages.shipments.title')}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className='max-w-[min(100%,28rem)] truncate'>
                {data.name}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {statusAlert != null && (
          <Alert variant={data.checkRunning ? 'info' : 'destructive'}>
            <AlertDescription>{statusAlert}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className='grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4'>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('shipmentsPage.table.lastChecked')}</ItemTitle>
            <ItemDescription>{lastCheckedDisplay}</ItemDescription>
          </ItemContent>
        </Item>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('shipmentsPage.table.skusLastChanged')}</ItemTitle>
            <ItemDescription>
              <span className='block'>
                {formatUpdatedAt(data.skusUpdatedAt, i18n.language)}
              </span>
              {data.latestActiveUser.length > 0 && (
                <span className='block text-muted-foreground'>
                  {t('shipmentsPage.detail.latestUpdateBy', {
                    user: data.latestActiveUser,
                  })}
                </span>
              )}
            </ItemDescription>
          </ItemContent>
        </Item>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('shipmentsPage.table.skuCount')}</ItemTitle>
            <ItemDescription>{data.skuCount}</ItemDescription>
          </ItemContent>
        </Item>
        <Item variant='outline'>
          <ItemContent>
            <ItemTitle>{t('shipmentsPage.table.note')}</ItemTitle>
            <ItemDescription>{noteDisplay}</ItemDescription>
          </ItemContent>
          {canWrite && (
            <ItemActions>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground'
                disabled={data.checkRunning}
                aria-label={t('shipmentsPage.detail.editNoteAria')}
                onClick={() => setEditor('note')}
              >
                <Pencil className='size-4' />
              </Button>
            </ItemActions>
          )}
        </Item>
      </div>

      <ShipmentCheckResult
        shipmentId={shipmentId}
        displayedCheckId={data.displayedCheck?.id ?? null}
        skusUpdatedAt={data.skusUpdatedAt}
        headerAction={
          canWrite ? (
            <>
              <Button
                type='button'
                size='sm'
                disabled={updateBusy}
                onClick={requestUpdate}
              >
                {data.checkRunning ? (
                  <Loader2 className='animate-spin' aria-hidden />
                ) : (
                  <RefreshCw aria-hidden />
                )}
                {t('shipmentsPage.detail.update')}
              </Button>
              <Button
                type='button'
                size='sm'
                variant='outline'
                disabled={data.checkRunning}
                onClick={() => setEditor('skus')}
              >
                <Upload aria-hidden />
                {t('shipmentsPage.detail.updateSkus')}
              </Button>
            </>
          ) : undefined
        }
      />

      <EditShipmentDialog
        shipmentId={shipmentId}
        open={editor != null}
        mode={editor ?? 'name'}
        name={data.name}
        note={data.note ?? ''}
        onOpenChange={(open) => {
          if (!open) {
            setEditor(null)
          }
        }}
      />
      <ConfirmDialog
        open={updateOpen}
        onOpenChange={setUpdateOpen}
        title={t('shipmentsPage.detail.updateConfirmTitle')}
        desc={t('shipmentsPage.detail.updateConfirmDesc')}
        cancelBtnText={t('shipmentsPage.detail.cancel')}
        confirmText={t('shipmentsPage.detail.update')}
        isLoading={startCheck.isPending}
        handleConfirm={() => {
          startCheck.mutate(undefined, {
            onSettled: () => {
              setUpdateOpen(false)
            },
          })
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        destructive
        title={t('shipmentsPage.detail.deleteConfirm', { name: data.name })}
        desc=''
        cancelBtnText={t('shipmentsPage.detail.cancel')}
        confirmText={t('shipmentsPage.detail.delete')}
        isLoading={deleteShipment.isPending}
        handleConfirm={() => {
          deleteShipment.mutate(undefined, {
            onSuccess: () => {
              navigate('/packaging/shipments')
            },
            onError: () => {
              toast.error(t('shipmentsPage.detail.deleteFailed'))
            },
          })
        }}
      />
    </div>
  )
}
